import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { useMediaQuery } from 'usehooks-ts';

import { Button } from '@ui/uikit/components/common/Button';
import Spinner from '@ui/uikit/components/common/Loader/Loader';

import { Typography } from '../Typography';
import { TableBody } from './TableBody';
import type { TableBodyCellProps } from './TableBodyCell';
import { TableHeader } from './TableHeader';
import { calculateColumns } from './utils/calculateColumns';
import { generateLoadingData } from './utils/generateLoadingData';

export type TablePaginationType = 'button' | 'infinite';

export interface TablePagination {
  hasNextPage: boolean;
  rowsPerPage: number;
  onClickShowMore?: () => void;
  isFetching: boolean;
  type?: TablePaginationType;
}

export type TableData = {
  id: string | number;
  [key: string]: any;
};

export type ColumnMeta<Data> = {
  name: keyof Data | string;
  label: string;
  hidden?: boolean;
};

export type TopColumnsType<Data> = Array<keyof Data>;

export interface TableColumn<Data> extends ColumnMeta<Data>, TableBodyCellProps {
  /**
   * Custom render function for the column cell
   * If not provided, the default value from `row[name]` will be used
   */
  render?: (
    params: {
      row: Data;
      rowIndex: number;
      colIndex: number;
    } & ColumnMeta<Data>,
  ) => ReactNode;
}

export interface TableProps<Data extends TableData> {
  /**
   * Array of column definitions
   * @default []
   */
  columns?: TableColumn<Data>[];
  /**
   * Array of data rows
   * @default []
   */
  data?: Data[];
  /**
   * Component to render when there is no data
   */
  emptyStateComponent?: ReactNode;
  /**
   * Custom grid template for desktop view
   */
  gridTemplateColumns?: CSSProperties['gridTemplateColumns'];
  /**
   * Array of column names to be shown at the top in mobile layout
   * @default column[0].name
   */
  topColumns?: TopColumnsType<Data>;
  /**
   * If `true`, the `Table` will display a skeleton loader
   * @default false
   */
  isLoading?: boolean;
  /**
   * Optional settings for pagination
   */
  pagination?: TablePagination;
  /**
   * Whether to disable mobile layout
   * @default false
   */
  disableMobileLayout?: boolean;
  /**
   * Whether to add padding on left and right sides
   * @default false
   */
  hasSidePadding?: boolean;
}

const DEFAULT_LOADING_ROWS_COUNT = 3;
const DEFAULT_ROW_HEIGHT = 56;
const DEFAULT_EMPTY_STATE_ROWS_HEIGHT = DEFAULT_LOADING_ROWS_COUNT * DEFAULT_ROW_HEIGHT;

export const Table = <Data extends TableData>({
  columns = [] as TableColumn<Data>[],
  data = [],
  disableMobileLayout = false,
  emptyStateComponent,
  gridTemplateColumns,
  isLoading = false,
  topColumns,
  pagination,
  hasSidePadding,
}: TableProps<Data>) => {
  const [currentPage, setCurrentPage] = useState(0);
  const hasInfinitePagination = pagination?.type === 'infinite';

  const displayedRows = useMemo(() => {
    return pagination?.rowsPerPage
      ? data.slice(0, (currentPage + 1) * pagination.rowsPerPage) ?? []
      : data;
  }, [data, currentPage, pagination?.rowsPerPage]);

  const tableData = isLoading
    ? generateLoadingData(columns, DEFAULT_LOADING_ROWS_COUNT)
    : displayedRows;

  const hasData = tableData.length > 0;

  const isMobileScreen = useMediaQuery('(max-width: 1023px)');
  const isMobileLayout = disableMobileLayout ? false : isMobileScreen;

  const { bodyColumns, headerColumns, columnCount, finalTopColumns } = useMemo(
    () => calculateColumns(columns, isMobileLayout, topColumns),
    [columns, topColumns, isMobileLayout],
  );

  const desktopTemplate = useMemo(
    () => gridTemplateColumns ?? `2fr repeat(${columnCount - 1}, 1fr)`,
    [columnCount, gridTemplateColumns],
  );

  const gridStyle = useMemo(
    () => ({
      display: 'grid',
      gridTemplateColumns: isMobileLayout
        ? `repeat(${headerColumns.length}, 1fr)`
        : desktopTemplate,
    }),
    [isMobileLayout, headerColumns.length, desktopTemplate],
  );

  const handleLoadMore = useCallback(async () => {
    pagination?.onClickShowMore?.();
    setCurrentPage((prev) => prev + 1);
  }, [pagination]);

  const canLoadMore = pagination?.hasNextPage || Boolean(data.length > displayedRows.length);

  const isShowMoreVisible = !hasInfinitePagination && canLoadMore;

  const showNextAmount = pagination?.hasNextPage
    ? pagination.rowsPerPage
    : Math.min(pagination?.rowsPerPage ?? 0, data.length - displayedRows.length);

  const hasHeaderLabels = headerColumns.some((column) => column.label?.trim().length > 0);

  const { ref: inViewRef } = useInView({
    onChange: (visible) => {
      if (!hasInfinitePagination || !pagination) {
        return;
      }

      if (visible && !pagination.isFetching && canLoadMore) {
        void handleLoadMore();
      }
    },
  });

  return (
    <div className="w-full h-full overflow-auto font-primary text-[14px] leading-[20px] font-medium flex flex-col items-center">
      {hasHeaderLabels && (
        <TableHeader columns={headerColumns} style={gridStyle} hasSidePadding={hasSidePadding} />
      )}
      {hasData ? (
        <>
          <TableBody
            columns={bodyColumns}
            data={tableData}
            style={gridStyle}
            isMobileLayout={isMobileLayout}
            topColumns={finalTopColumns}
            isLoading={isLoading}
            hasSidePadding={hasSidePadding}
          />
          {isShowMoreVisible && pagination && (
            <div className="max-sm-msq:w-full mt-6 self-center">
              <Button
                {...(isMobileLayout ? { width: '100%' } : {})}
                variant="secondary"
                size="medium"
                onClick={handleLoadMore}
                disabled={pagination.isFetching ?? false}
              >
                {`Show Next ${showNextAmount}`}
              </Button>
            </div>
          )}

          {hasInfinitePagination && (
            <div ref={inViewRef} className="flex items-center p-4">
              {pagination?.isFetching && <Spinner size="md" />}
            </div>
          )}
        </>
      ) : (
        <div
          className="flex w-full items-center justify-center h-full border-b border-base-border-light"
          style={{
            height: DEFAULT_EMPTY_STATE_ROWS_HEIGHT,
          }}
        >
          {emptyStateComponent ?? (
            <Typography variant="caption-s" color="tertiary" textAlign="center">
              No Results
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};
